import { useQuery } from "@tanstack/react-query";
import { GlassCard } from "@/components/ui/glass-card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Settings2, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

interface DeviceConfigurationsProps {
  deviceId: string;
}

const DeviceConfigurations = ({ deviceId }: DeviceConfigurationsProps) => {
  const { data: configurations } = useQuery({
    queryKey: ['device-configurations', deviceId],
    queryFn: async () => {
      const { data: deployments, error: deploymentsError } = await supabase
        .from('script_deployments')
        .select(`
          *,
          configurations (
            id,
            name,
            description,
            script_type
          )
        `)
        .eq('device_id', deviceId);

      if (deploymentsError) throw deploymentsError;

      const { data: requests, error: requestsError } = await supabase
        .from('privilege_elevation_requests')
        .select(`
          *,
          privilege_elevation_rules (
            id,
            name,
            description,
            duration_minutes,
            requires_approval
          )
        `)
        .eq('device_id', deviceId);

      if (requestsError) throw requestsError;

      return {
        deployments,
        requests
      };
    }
  });

  return (
    <GlassCard variant="default" hover={false} className="p-6">
      <h2 className="text-xl font-semibold mb-4 text-slate-900 dark:text-white">Device Configurations</h2>
      <ScrollArea className="h-[500px] pr-4">
        <div className="space-y-6">
          <div>
            <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-blue-600" />
              Script Configurations
            </h3>
            <div className="space-y-3">
              {configurations?.deployments.map((deployment) => (
                <div
                  key={deployment.id}
                  className="p-4 rounded-lg border border-slate-200 dark:border-slate-700"
                >
                  <p className="font-medium text-slate-900 dark:text-white">
                    {deployment.configurations?.name || 'Unnamed Configuration'}
                  </p>
                  {deployment.configurations?.description && (
                    <p className="text-sm text-slate-500 mt-1">
                      {deployment.configurations.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                      {deployment.configurations?.script_type}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      deployment.status === 'success' ? 'bg-green-100 text-green-700' :
                      deployment.status === 'error' ? 'bg-red-100 text-red-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {deployment.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!configurations?.deployments || configurations.deployments.length === 0) && (
                <p className="text-slate-500 text-center py-4">No script configurations found</p>
              )}
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-3 flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" />
              Privilege Rules
            </h3>
            <div className="space-y-3">
              {configurations?.requests.map((request) => (
                <div
                  key={request.id}
                  className="p-4 rounded-lg border border-slate-200 dark:border-slate-700"
                >
                  <p className="font-medium text-slate-900 dark:text-white">
                    {request.privilege_elevation_rules?.name || 'Unnamed Rule'}
                  </p>
                  {request.privilege_elevation_rules?.description && (
                    <p className="text-sm text-slate-500 mt-1">
                      {request.privilege_elevation_rules.description}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-2">
                    <span className="text-xs px-2 py-1 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                      {request.privilege_elevation_rules?.duration_minutes} minutes
                    </span>
                    <span className={`text-xs px-2 py-1 rounded-full ${
                      request.status === 'approved' ? 'bg-green-100 text-green-700' :
                      request.status === 'rejected' ? 'bg-red-100 text-red-700' :
                      request.status === 'expired' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {request.status}
                    </span>
                  </div>
                </div>
              ))}
              {(!configurations?.requests || configurations.requests.length === 0) && (
                <p className="text-slate-500 text-center py-4">No privilege rules found</p>
              )}
            </div>
          </div>
        </div>
      </ScrollArea>
    </GlassCard>
  );
};

export default DeviceConfigurations;